<template>
  <main class="signed-page">
    <div class="signed-content">
      <img
          class="signed-logo"
          :src="logo"
          alt="Chèque Intermittents"
      />

      <Transition name="fade" mode="out-in">
        <!-- Loading screen -->
        <section
            v-if="loading"
            key="loading"
            class="signed-state"
        >
          <div
              class="loader"
              role="status"
              aria-label="Chargement en cours"
          ></div>

          <p class="loading-message">
            Veuillez patienter quelques secondes, nous<br />
            peaufinons les derniers détails en coulisses...
          </p>
        </section>

        <!-- Success screen -->
        <section
            v-else
            key="success"
            class="signed-state success-state"
        >
          <img
              class="success-illustration"
              :src="successIllustration"
              alt=""
          />

          <p class="success-title">
            Bravo, votre signature a été validée
          </p>

          <p class="success-message">
            Vous pouvez maintenant fermer cet onglet<br />
            et retourner sur votre espace privé
          </p>
        </section>
      </Transition>
    </div>
  </main>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import EduSign from "@/generated/classes/EduSign";

// Replace these paths with the real paths of your assets.
import logo from "@/assets/LogoCI.png";
import successIllustration from "@/assets/signature-success.png";

export default defineComponent({
  name: "SignedPage",

  data() {
    return {
      loading: false,
      logo,
      successIllustration
    };
  },

  async mounted() {
    const signingSession = this.getEdusignSession();
    if (!signingSession) {
      return;
    }

    this.loading = true;
    try {
      // The server performs the 15-second polling loop. The client sends only
      // this request and waits for the reconciliation result.
      await EduSign.edusignPolling(signingSession.documentId);
    } catch {
      // The redirect already confirms that EduSign accepted the signature.
      // A later signature-link request can perform the one-shot reconciliation.
    } finally {
      sessionStorage.removeItem("edusignSigningInProgress");
      this.loading = false;
    }
  },

  methods: {
    getEdusignSession(): { documentId: number; expiresAt: number } | null {
      const key = "edusignSigningInProgress";
      const storedValue = sessionStorage.getItem(key);
      if (storedValue) {
        try {
          const state = JSON.parse(storedValue) as {
            documentId?: number;
            expiresAt?: number;
          };
          if (
              typeof state.documentId === "number" &&
              state.documentId > 0 &&
              typeof state.expiresAt === "number" &&
              state.expiresAt > Date.now()
          ) {
            return {
              documentId: state.documentId,
              expiresAt: state.expiresAt
            };
          }
        } catch {
          // Fall back to the document ID included in the Edusign return URL.
        }
        sessionStorage.removeItem(key);
      }

      const queryDocumentId = Number.parseInt(
          new URLSearchParams(window.location.search).get("documentId") ?? "",
          10
      );
      if (Number.isSafeInteger(queryDocumentId) && queryDocumentId > 0) {
        return {
          documentId: queryDocumentId,
          expiresAt: Date.now() + 30 * 60 * 1000
        };
      }

      return null;
    }
  }
});
</script>

<style scoped>
.signed-page {
  display: flex;
  align-items: center;
  justify-content: center;

  width: 100%;
  min-height: 100vh;

  margin: 0;
  padding: 24px;

  background: #ffffff;
  box-sizing: border-box;
}

.signed-content {
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
  max-width: 460px;

  text-align: center;
  transform: translateY(-3vh);
}

.signed-logo {
  display: block;

  width: 154px;
  height: auto;

  margin-bottom: 52px;
  object-fit: contain;
}

.signed-state {
  display: flex;
  flex-direction: column;
  align-items: center;

  width: 100%;
}

.loader {
  width: 82px;
  height: 82px;

  margin-bottom: 54px;

  border: 9px solid #f2f3f4;
  border-top-color: #d8dcde;
  border-radius: 50%;

  box-sizing: border-box;
  animation: rotate-loader 0.9s linear infinite;
}

.loading-message {
  margin: 0;

  color: #212121;

  font-family: Inter, Arial, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.25;
}

.success-state {
  margin-top: -17px;
}

.success-illustration {
  display: block;

  width: 140px;
  height: 150px;

  margin-bottom: 25px;

  object-fit: contain;
}

.success-title {
  margin: 0 0 12px;

  color: #202020;

  font-family: Inter, Arial, sans-serif;
  font-size: 15px;
  font-weight: 500;
  line-height: 1.3;
}

.success-message {
  margin: 0;

  color: #24717a;

  font-family: Inter, Arial, sans-serif;
  font-size: 15px;
  font-weight: 400;
  line-height: 1.45;
}

.fade-enter-active,
.fade-leave-active {
  transition:
      opacity 250ms ease,
      transform 250ms ease;
}

.fade-enter-from {
  opacity: 0;
  transform: translateY(8px);
}

.fade-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

@keyframes rotate-loader {
  from {
    transform: rotate(0deg);
  }

  to {
    transform: rotate(360deg);
  }
}

@media (max-width: 600px) {
  .signed-page {
    padding: 20px;
  }

  .signed-content {
    transform: translateY(-2vh);
  }

  .signed-logo {
    width: 145px;
    margin-bottom: 46px;
  }

  .loader {
    width: 74px;
    height: 74px;

    margin-bottom: 45px;
    border-width: 8px;
  }

  .loading-message,
  .success-title,
  .success-message {
    font-size: 14px;
  }
}
</style>
